import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Chip, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ConfirmButton,
  EmptyState,
  ErrorBox,
  ListField,
  Loading,
  Page,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { formatRelative } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import {
  automationGw,
  type Agent,
  type AutomationGwApi,
  type ErrorAction,
  type FileWatcher,
  type FileWatcherInput,
  type PostAction,
} from '../api/client';
import { useAutomationTarget, useAutomationWrite } from '../shared';
import '../automation.css';

const POST_ACTIONS: { value: PostAction; label: string }[] = [
  { value: 'delete', label: 'Delete the file' },
  { value: 'rename', label: 'Rename with a suffix' },
  { value: 'movetosubfolder', label: 'Move to a subfolder' },
  { value: 'none', label: 'Leave it' },
];
const ERROR_ACTIONS: { value: ErrorAction; label: string }[] = [
  { value: 'retryandmove', label: 'Retry, then move to the error folder' },
  { value: 'retryandleave', label: 'Retry, then leave it' },
  { value: 'skipandlog', label: 'Skip and log' },
];

const EMPTY: FileWatcherInput = {
  clientId: '',
  name: '',
  smbUri: '',
  username: '',
  password: '',
  fileFilters: ['*.*'],
  destinationUrl: '',
  destinationHeaders: {},
  enabled: true,
  watchEnabled: true,
  postAction: 'delete',
  renameSuffix: '.processed',
  postActionSubfolder: 'processed',
  errorAction: 'retryandmove',
  maxRetries: 3,
  errorSubfolder: 'error',
  allowedNamespaces: [],
  processExistingOnStartup: true,
  pollingIntervalSeconds: 30,
  fileStabilityCheck: true,
  fileStabilityTimeSeconds: 2,
  fileStabilityTimeoutSeconds: 60,
  postThroughTunnel: false,
};

const toInput = (w: FileWatcher): FileWatcherInput => {
  const { id: _id, hasPassword: _p, createdAt: _c, updatedAt: _u, ...rest } = w;
  return { ...EMPTY, ...rest, password: '', clearPassword: false };
};

const headersToText = (h: Record<string, string>) =>
  Object.entries(h)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
const textToHeaders = (s: string) =>
  Object.fromEntries(
    s
      .split('\n')
      .map((l) => l.split(/:(.*)/s).map((x) => x.trim()))
      .filter(([k]) => k)
      .map(([k, v]) => [k, v ?? ''])
  );

/** SMB shares the agents poll; new files are posted to a destination URL (directly or through the tunnel). */
export function FileWatchersPage() {
  const target = useAutomationTarget();
  const api = useMemo(() => automationGw(target), [target]);
  const canWrite = useAutomationWrite();
  const toast = useNotice();
  const [agent, setAgent] = useParam('agent', '');
  const data = useAsync(async () => {
    const [watchers, agents] = await Promise.all([api.fileWatchers(), api.agents()]);
    return { watchers, agents };
  }, [api]);
  const [editing, setEditing] = useState<{
    id: string | null;
    draft: FileWatcherInput;
    hasPassword: boolean;
  } | null>(null);
  const d = data.data;
  const agentOf = (id: string) => d?.agents.find((a) => a.id === id);
  const rows = (d?.watchers ?? [])
    .filter((w) => !agent || w.clientId === agent)
    .sort((a, b) => a.name.localeCompare(b.name));
  const remove = async (w: FileWatcher) => {
    try {
      await api.deleteFileWatcher(w.id);
      toast.success(`Deleted ${w.name}`);
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="File watchers"
      description="SMB shares the agents watch. Each new file is posted to the destination URL, then deleted, renamed or moved."
      actions={
        <>
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={data.loading}
            onClick={() => void data.reload()}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button
              size="sm"
              onClick={() =>
                setEditing({ id: null, draft: { ...EMPTY, clientId: agent }, hasPassword: false })
              }
            >
              New file watcher
            </Button>
          )}
        </>
      }
    >
      {toast.element}
      <div className="ag-toolbar">
        <Select
          aria-label="Agent"
          value={agent}
          onChange={setAgent}
          options={[
            { value: '', label: `All agents (${d?.agents.length ?? 0})` },
            ...(d?.agents ?? []).map((a) => ({ value: a.id, label: `${a.displayName} (${a.clientId})` })),
          ]}
        />
      </div>
      <ErrorBox error={data.error} />
      <div className="nyk-card">
        <Loading loading={data.loading && !d} />
        {!!rows.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="ag-watchers">
              <thead>
                <tr>
                  <th>Watcher</th>
                  <th>Agent</th>
                  <th>Share</th>
                  <th>Posts to</th>
                  <th>After</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.id} data-watcher={w.id}>
                    <td className="nyk-card-title">
                      <b>{w.name}</b>{' '}
                      {!w.enabled ? (
                        <Chip size="sm">disabled</Chip>
                      ) : (
                        !w.watchEnabled && <Chip size="sm">not watching</Chip>
                      )}
                      <div className="nyk-ui-hint">updated {formatRelative(w.updatedAt ?? w.createdAt)}</div>
                    </td>
                    <td data-label="Agent">{agentOf(w.clientId)?.displayName ?? w.clientId}</td>
                    <td data-label="Share">
                      <span className="nyk-mono">{w.smbUri}</span>
                      <div className="nyk-ui-hint">
                        {w.fileFilters.join(', ')} · every {w.pollingIntervalSeconds} s
                        {w.username ? ` · as ${w.username}` : ''}
                      </div>
                    </td>
                    <td data-label="Posts to">
                      <span className="nyk-mono">{w.destinationUrl}</span>
                      {w.postThroughTunnel && <div className="nyk-ui-hint">through the tunnel</div>}
                    </td>
                    <td data-label="After">
                      {POST_ACTIONS.find((p) => p.value === w.postAction)?.label ?? w.postAction}
                    </td>
                    <td className="nyk-row-actions">
                      {canWrite && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setEditing({ id: w.id, draft: toInput(w), hasPassword: w.hasPassword })
                            }
                          >
                            Edit
                          </Button>
                          <ConfirmButton
                            size="sm"
                            variant="ghost"
                            message={`Delete the file watcher ${w.name}?`}
                            onConfirm={() => void remove(w)}
                          >
                            Delete
                          </ConfirmButton>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
        {d && !rows.length && <EmptyState title="No file watchers." />}
      </div>
      {editing && d && (
        <WatcherDialog
          api={api}
          id={editing.id}
          initial={editing.draft}
          hasPassword={editing.hasPassword}
          agents={d.agents}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success('File watcher saved');
            void data.reload();
          }}
        />
      )}
    </Page>
  );
}

function WatcherDialog({
  api,
  id,
  initial,
  hasPassword,
  agents,
  onClose,
  onSaved,
}: {
  api: AutomationGwApi;
  id: string | null;
  initial: FileWatcherInput;
  hasPassword: boolean;
  agents: Agent[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [headers, setHeaders] = useState(() => headersToText(initial.destinationHeaders));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<FileWatcherInput>) => setDraft({ ...draft, ...patch });
  const num = (v: string) => (v.trim() === '' ? 0 : Number(v));
  const save = async () => {
    if (!draft.clientId) return setError('Pick an agent.');
    if (!draft.name.trim() || !draft.smbUri.trim() || !draft.destinationUrl.trim())
      return setError('Name, share and destination URL are required.');
    setBusy(true);
    setError(null);
    try {
      const body = {
        ...draft,
        destinationHeaders: textToHeaders(headers),
        password: draft.password || undefined,
      };
      if (id) await api.updateFileWatcher(id, body);
      else await api.createFileWatcher(body);
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={id ? `Edit ${initial.name}` : 'New file watcher'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="ag-form">
        <ErrorBox error={error} />
        <Select
          label="Agent"
          value={draft.clientId}
          onChange={(v) => set({ clientId: v })}
          options={[
            { value: '', label: 'Pick an agent' },
            ...agents.map((a) => ({ value: a.id, label: `${a.displayName} (${a.clientId})` })),
          ]}
        />
        <TextField label="Name" value={draft.name} onChange={(e) => set({ name: e.target.value })} />
        <TextField
          label="SMB share"
          placeholder="smb://server/share/folder"
          value={draft.smbUri}
          onChange={(e) => set({ smbUri: e.target.value })}
        />
        <div className="ag-form-row">
          <TextField
            label="Username"
            autoComplete="off"
            value={draft.username ?? ''}
            onChange={(e) => set({ username: e.target.value })}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            hint={hasPassword ? 'Stored; empty keeps it.' : undefined}
            value={draft.password ?? ''}
            onChange={(e) => set({ password: e.target.value, clearPassword: false })}
          />
        </div>
        {hasPassword && (
          <Switch
            checked={!!draft.clearPassword}
            onChange={(v) => set({ clearPassword: v, password: '' })}
            label="Remove the stored password"
          />
        )}
        <ListField
          label="File filters"
          hint="Comma-separated patterns, e.g. *.xml, *.csv"
          value={draft.fileFilters}
          onChange={(v) => set({ fileFilters: v })}
        />
        <TextField
          label="Destination URL"
          value={draft.destinationUrl}
          onChange={(e) => set({ destinationUrl: e.target.value })}
        />
        <TextField
          label="Destination headers"
          hint="One per line: Name: value"
          multiline
          rows={3}
          value={headers}
          onChange={(e) => setHeaders(e.target.value)}
        />
        <Switch
          checked={draft.postThroughTunnel}
          onChange={(v) => set({ postThroughTunnel: v })}
          label="Post through the tunnel (the gateway posts to a cluster-internal URL)"
        />
        <Select
          label="After a successful post"
          value={draft.postAction}
          onChange={(v) => set({ postAction: v as PostAction })}
          options={POST_ACTIONS}
        />
        {draft.postAction === 'rename' && (
          <TextField
            label="Rename suffix"
            value={draft.renameSuffix ?? ''}
            onChange={(e) => set({ renameSuffix: e.target.value })}
          />
        )}
        {draft.postAction === 'movetosubfolder' && (
          <TextField
            label="Subfolder"
            value={draft.postActionSubfolder ?? ''}
            onChange={(e) => set({ postActionSubfolder: e.target.value })}
          />
        )}
        <div className="ag-form-row">
          <Select
            label="On error"
            value={draft.errorAction}
            onChange={(v) => set({ errorAction: v as ErrorAction })}
            options={ERROR_ACTIONS}
          />
          <TextField
            label="Retries"
            type="number"
            inputMode="numeric"
            value={draft.maxRetries}
            onChange={(e) => set({ maxRetries: num(e.target.value) })}
          />
          {draft.errorAction === 'retryandmove' && (
            <TextField
              label="Error subfolder"
              value={draft.errorSubfolder ?? ''}
              onChange={(e) => set({ errorSubfolder: e.target.value })}
            />
          )}
        </div>
        <div className="ag-form-row">
          <TextField
            label="Poll every (s)"
            type="number"
            inputMode="numeric"
            value={draft.pollingIntervalSeconds}
            onChange={(e) => set({ pollingIntervalSeconds: num(e.target.value) })}
          />
          <TextField
            label="Stable for (s)"
            type="number"
            inputMode="numeric"
            value={draft.fileStabilityTimeSeconds}
            onChange={(e) => set({ fileStabilityTimeSeconds: num(e.target.value) })}
          />
          <TextField
            label="Stability timeout (s)"
            type="number"
            inputMode="numeric"
            value={draft.fileStabilityTimeoutSeconds}
            onChange={(e) => set({ fileStabilityTimeoutSeconds: num(e.target.value) })}
          />
        </div>
        <ListField
          label="Allowed namespaces"
          hint="Namespaces that may use the file API on this share; empty = any."
          value={draft.allowedNamespaces}
          onChange={(v) => set({ allowedNamespaces: v })}
        />
        <Switch
          checked={draft.fileStabilityCheck}
          onChange={(v) => set({ fileStabilityCheck: v })}
          label="Wait until a file stops changing"
        />
        <Switch
          checked={draft.processExistingOnStartup}
          onChange={(v) => set({ processExistingOnStartup: v })}
          label="Process files already there at startup"
        />
        <Switch checked={draft.watchEnabled} onChange={(v) => set({ watchEnabled: v })} label="Watching" />
        <Switch checked={draft.enabled} onChange={(v) => set({ enabled: v })} label="Enabled" />
      </div>
    </Dialog>
  );
}
